import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { devices, type Zariadenie } from '../api/client.js';
import { Notice, Slider, StatusDot, Toggle, type Tone } from '../components/ui.js';
import { nazovSchopnosti } from './common.js';

/*
 * Ovladanie zariadenia.
 *
 * Rozhranie reaguje hned (optimisticky), lebo cesta prikazu vedie cez
 * zbernicu az do zariadenia a potvrdenie moze trvat stovky milisekund.
 * Ked prikaz zlyha, hodnota sa vrati spat a zobrazi sa hlaska - tichy
 * navrat by vyzeral ako zaseknute rozhranie.
 */

interface Ovladanie {
  readonly hodnoty: Record<string, unknown>;
  readonly chyba: string | undefined;
  readonly prebieha: boolean;
  posli(capability: string, value: unknown): void;
  nastavLokalne(capability: string, value: unknown): void;
}

export function useOvladanie(device: Zariadenie, poZmene?: () => void): Ovladanie {
  const [hodnoty, setHodnoty] = useState<Record<string, unknown>>({ ...device.state });
  const [chyba, setChyba] = useState<string | undefined>(undefined);
  const [prebieha, setPrebieha] = useState(false);

  /*
   * Zosuladenie so serverom.
   *
   * Po kliknuti sa hodnota zobrazi hned (optimisticky), lenze zariadenie
   * ju potvrdi az o chvilu. Kym potvrdenie nepride, server hlasi STARU
   * hodnotu - a keby sa ňou prepisala tá zobrazena, prepinac by skocil
   * spat a vyzeralo by to, ze prvé kliknutie nič neurobilo.
   *
   * Preto sa preberá len hodnota, ktorá sa na SERVERI naozaj zmenila:
   * porovnáva sa s tým, čo server hlásil naposledy, nie s tým, čo je
   * na obrazovke.
   */
  const posledneZoServera = useRef<Record<string, unknown>>({ ...device.state });

  useEffect(() => {
    const zmenene: Record<string, unknown> = {};
    for (const [capability, hodnota] of Object.entries(device.state)) {
      if (!Object.is(posledneZoServera.current[capability], hodnota)) {
        zmenene[capability] = hodnota;
      }
    }
    posledneZoServera.current = { ...device.state };
    if (Object.keys(zmenene).length > 0) setHodnoty((h) => ({ ...h, ...zmenene }));
  }, [device.state]);

  const nastavLokalne = useCallback((capability: string, value: unknown) => {
    setHodnoty((h) => ({ ...h, [capability]: value }));
  }, []);

  const posli = useCallback(
    (capability: string, value: unknown) => {
      const predtym = hodnoty[capability];
      setHodnoty((h) => ({ ...h, [capability]: value }));
      setChyba(undefined);
      setPrebieha(true);

      void devices
        .command(device.id, capability, value)
        .then(() => {
          poZmene?.();
        })
        .catch((error: unknown) => {
          setHodnoty((h) => ({ ...h, [capability]: predtym }));
          setChyba(error instanceof Error ? error.message : 'Príkaz sa nepodarilo odoslať.');
        })
        .finally(() => {
          setPrebieha(false);
        });
    },
    [device.id, hodnoty, poZmene],
  );

  return { hodnoty, chyba, prebieha, posli, nastavLokalne };
}

interface Hsv {
  readonly h: number;
  readonly s: number;
  readonly v: number;
}

/** Hodnota schopnosti `Color`, alebo predvolena, ked este nie je znama. */
function farbaZo(hodnota: unknown): Hsv {
  if (hodnota !== null && typeof hodnota === 'object') {
    const o = hodnota as Record<string, unknown>;
    if (typeof o['h'] === 'number' && typeof o['s'] === 'number' && typeof o['v'] === 'number') {
      return { h: o['h'], s: o['s'], v: o['v'] };
    }
  }
  return { h: 30, s: 80, v: 100 };
}

/**
 * Vyber farby.
 *
 * Odtien je posuvnik na farebnom prechode, sytost druhy posuvnik nad
 * prechodom od sedej po zvoleny odtien. Kruhove koliesko by na mobile
 * bez presneho ukazovatela bolo horsie ovladatelne nez dva posuvniky.
 *
 * Jas sa tu NENASTAVUJE: ma vlastnu schopnost `Brightness` a miesat obe
 * by znamenalo dve miesta, ktore hovoria to iste.
 */
function VyberFarby({
  hodnota,
  disabled,
  onChange,
  onCommit,
}: {
  readonly hodnota: Hsv;
  readonly disabled: boolean;
  readonly onChange: (farba: Hsv) => void;
  readonly onCommit: (farba: Hsv) => void;
}): ReactNode {
  const nahlad = `hsl(${hodnota.h} ${hodnota.s}% 50%)`;
  const prechodSytosti = `linear-gradient(to right, hsl(${hodnota.h} 0% 50%), hsl(${hodnota.h} 100% 50%))`;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm text-(--color-text-muted)">{nazovSchopnosti('Color')}</span>
        <span
          aria-hidden="true"
          className="size-8 rounded-(--radius-control) border border-(--color-border-strong)"
          style={{ background: nahlad }}
        />
      </div>

      <label className="flex flex-col gap-1">
        <span className="text-xs text-(--color-text-subtle)">Odtieň</span>
        <input
          type="range"
          min={0}
          max={360}
          value={Math.round(hodnota.h)}
          disabled={disabled}
          aria-label="Odtieň farby"
          onChange={(e) => onChange({ ...hodnota, h: Number(e.target.value) })}
          onMouseUp={(e) => onCommit({ ...hodnota, h: Number(e.currentTarget.value) })}
          onTouchEnd={(e) => onCommit({ ...hodnota, h: Number(e.currentTarget.value) })}
          onKeyUp={(e) => onCommit({ ...hodnota, h: Number(e.currentTarget.value) })}
          style={{
            background:
              'linear-gradient(to right, hsl(0 90% 50%), hsl(60 90% 50%), hsl(120 90% 50%), hsl(180 90% 50%), hsl(240 90% 50%), hsl(300 90% 50%), hsl(360 90% 50%))',
            borderRadius: 'var(--radius-pill)',
          }}
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-xs text-(--color-text-subtle)">Sýtosť {Math.round(hodnota.s)} %</span>
        <input
          type="range"
          min={0}
          max={100}
          value={Math.round(hodnota.s)}
          disabled={disabled}
          aria-label="Sýtosť farby"
          onChange={(e) => onChange({ ...hodnota, s: Number(e.target.value) })}
          onMouseUp={(e) => onCommit({ ...hodnota, s: Number(e.currentTarget.value) })}
          onTouchEnd={(e) => onCommit({ ...hodnota, s: Number(e.currentTarget.value) })}
          onKeyUp={(e) => onCommit({ ...hodnota, s: Number(e.currentTarget.value) })}
          style={{ background: prechodSytosti, borderRadius: 'var(--radius-pill)' }}
        />
      </label>
    </div>
  );
}

const cislo = (hodnota: unknown, predvolena: number): number =>
  typeof hodnota === 'number' && Number.isFinite(hodnota) ? hodnota : predvolena;

/** Da sa dana schopnost ovladat? */
const zapisovatelna = (device: Zariadenie, name: string): boolean =>
  device.capabilities.some(
    (c) => c.name === name && (c.access === 'write' || c.access === 'readwrite'),
  );

export function PanelOvladania({
  device,
  ovladanie,
  compact = false,
}: {
  readonly device: Zariadenie;
  readonly ovladanie: Ovladanie;
  readonly compact?: boolean;
}): ReactNode {
  /*
   * Ovladanie sa vypina LEN pri potvrdenej nedostupnosti.
   *
   * `unknown` znamena „nevieme", nie „nefunguje" - napriklad kym most
   * Zigbee2MQTT neposlal dostupnost. Zakazat pri nom ovladanie by
   * znamenalo, ze pouzivatel nezapne svetlo, ktore v skutocnosti
   * funguje; prikaz sa aj tak posiela a jeho zlyhanie sa ohlasi.
   */
  const nedostupne = device.availability.state === 'offline';
  const neznamy = device.availability.state === 'unknown';
  const { hodnoty, posli, nastavLokalne } = ovladanie;

  const maOnOff = zapisovatelna(device, 'OnOff');
  const maJas = zapisovatelna(device, 'Brightness');
  const maTeplotu = zapisovatelna(device, 'ColorTemperature');
  const maPolohu = zapisovatelna(device, 'Position');
  const maFarbu = zapisovatelna(device, 'Color');

  if (!maOnOff && !maJas && !maTeplotu && !maPolohu && !maFarbu) {
    return compact ? null : (
      <p className="text-sm text-(--color-text-muted)">Toto zariadenie sa nedá ovládať.</p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {maOnOff && (
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-(--color-text-muted)">{nazovSchopnosti('OnOff')}</span>
          <Toggle
            label={`${hodnoty['OnOff'] === true ? 'Vypnúť' : 'Zapnúť'} ${device.name}`}
            checked={hodnoty['OnOff'] === true}
            disabled={nedostupne}
            onChange={(v) => posli('OnOff', v)}
          />
        </div>
      )}

      {maJas && !compact && (
        <Slider
          label={nazovSchopnosti('Brightness')}
          unit=" %"
          min={1}
          max={100}
          value={cislo(hodnoty['Brightness'], 100)}
          disabled={nedostupne}
          onChange={(v) => nastavLokalne('Brightness', v)}
          onCommit={(v) => posli('Brightness', v)}
        />
      )}

      {maTeplotu && !compact && (
        <Slider
          label={nazovSchopnosti('ColorTemperature')}
          unit=" K"
          min={2700}
          max={6500}
          step={100}
          value={cislo(hodnoty['ColorTemperature'], 4000)}
          disabled={nedostupne}
          onChange={(v) => nastavLokalne('ColorTemperature', v)}
          onCommit={(v) => posli('ColorTemperature', v)}
        />
      )}

      {maFarbu && !compact && (
        <VyberFarby
          hodnota={farbaZo(hodnoty['Color'])}
          disabled={nedostupne}
          onChange={(farba) => nastavLokalne('Color', farba)}
          onCommit={(farba) => posli('Color', farba)}
        />
      )}

      {maPolohu && !compact && (
        <Slider
          label={nazovSchopnosti('Position')}
          unit=" %"
          min={0}
          max={100}
          value={cislo(hodnoty['Position'], 0)}
          disabled={nedostupne}
          onChange={(v) => nastavLokalne('Position', v)}
          onCommit={(v) => posli('Position', v)}
        />
      )}

      {ovladanie.chyba !== undefined && <Notice tone="danger">{ovladanie.chyba}</Notice>}

      {nedostupne && !compact && (
        <p className="text-xs text-(--color-text-subtle)">
          Zariadenie neodpovedá, ovládanie je dočasne vypnuté.
        </p>
      )}

      {neznamy && !compact && (
        <p className="text-xs text-(--color-text-subtle)">
          O dostupnosti zariadenia zatiaľ nič nevieme. Ovládanie skús — ak príkaz neprejde, povie
          to.
        </p>
      )}
    </div>
  );
}

/** Hodnoty, ktore sa len citaju - merania zo zasuvky a podobne. */
/*
 * Stavy, ktore sa nedaju ovladat ani zmerat.
 *
 * Senzor pritomnosti, okenny kontakt a tlacidlo nemaju v rozhrani co
 * ovladat a nie su ani meranie s jednotkou - dovtedy sa preto nikde
 * neukazovali a zariadenie vyzeralo prazdne, hoci hodnoty poslalo.
 */
const POPIS_STAVU: Record<
  string,
  { readonly ano: string; readonly nie: string; readonly tonAno: Tone }
> = {
  /*
   * Pohyb je červený zámerne.
   *
   * Je to jediný z týchto stavov, ktorý sa deje TERAZ a hneď zase
   * prestane — v zozname ho treba zachytiť očami za pochodu. Zapnuté
   * svetlo alebo zatvorený kontakt sú pokojné stavy, tie modrú stačí.
   */
  Occupancy: { ano: 'Pohyb', nie: 'Kľud', tonAno: 'danger' },
  Contact: { ano: 'Zatvorené', nie: 'Otvorené', tonAno: 'accent' },
  OnOff: { ano: 'Zapnuté', nie: 'Vypnuté', tonAno: 'accent' },
};

/** Stav senzora ako dvojica popis + ton; `undefined`, ked hodnota nie je. */
export function stavSenzora(
  device: Zariadenie,
  name: string,
): { readonly popis: string; readonly zapnute: boolean; readonly ton: Tone } | undefined {
  const hodnota = device.state[name];
  if (typeof hodnota !== 'boolean') return undefined;
  const popis = POPIS_STAVU[name];
  if (popis === undefined) return undefined;
  return {
    popis: hodnota ? popis.ano : popis.nie,
    zapnute: hodnota,
    ton: hodnota ? popis.tonAno : 'neutral',
  };
}

/** Ktore schopnosti sa v prehlade ukazuju ako stav, nie ako meranie. */
export function stavoveSchopnosti(device: Zariadenie): string[] {
  return device.capabilities
    .filter((c) => c.access === 'read' && c.name in POPIS_STAVU)
    .map((c) => c.name);
}

/** Ma zariadenie co ukazat v karte stavu? */
export function maStavy(device: Zariadenie): boolean {
  if (device.lastAction !== null) return true;
  return stavoveSchopnosti(device).some((name) => stavSenzora(device, name) !== undefined);
}

export function Stavy({ device }: { readonly device: Zariadenie }): ReactNode {
  const stavy = stavoveSchopnosti(device)
    .map((name) => ({ name, stav: stavSenzora(device, name) }))
    .filter((s) => s.stav !== undefined);

  const akcia = device.lastAction;
  if (stavy.length === 0 && akcia === null) return null;

  return (
    <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {stavy.map((s) => (
        <div key={s.name} className="flex flex-col">
          <dt className="text-xs text-(--color-text-subtle)">{nazovSchopnosti(s.name)}</dt>
          <dd className="flex items-center gap-1.5 text-lg font-medium text-(--color-text)">
            <StatusDot tone={s.stav?.ton ?? 'neutral'} />
            {s.stav?.popis}
          </dd>
        </div>
      ))}
      {akcia !== null && (
        <div className="flex flex-col">
          <dt className="text-xs text-(--color-text-subtle)">Naposledy stlačené</dt>
          <dd className="text-lg font-medium text-(--color-text)">
            {akcia.value}
            <span className="ml-1 block text-xs font-normal text-(--color-text-subtle)">
              {new Date(akcia.at).toLocaleString('sk-SK')}
            </span>
          </dd>
        </div>
      )}
    </dl>
  );
}

/**
 * Kody pocasia podla WMO, tak ako ich posiela Open-Meteo.
 *
 * Holé číslo nič nepovie, preto sa k nemu píše text. Zoznam je
 * z normy WMO 4677 v rozsahu, ktorý Open-Meteo skutočne vydáva —
 * nedopĺňam doň kódy, ktoré by sme nikdy nedostali.
 */
const POPIS_POCASIA: Record<number, string> = {
  0: 'Jasno',
  1: 'Prevažne jasno',
  2: 'Polojasno',
  3: 'Zamračené',
  45: 'Mlha',
  48: 'Mlha s námrazou',
  51: 'Slabé mrholenie',
  53: 'Mrholenie',
  55: 'Silné mrholenie',
  56: 'Mrznúce mrholenie',
  57: 'Silné mrznúce mrholenie',
  61: 'Slabý dážď',
  63: 'Dážď',
  65: 'Silný dážď',
  66: 'Mrznúci dážď',
  67: 'Silný mrznúci dážď',
  71: 'Slabé sneženie',
  73: 'Sneženie',
  75: 'Silné sneženie',
  77: 'Snehové krúpky',
  80: 'Slabé prehánky',
  81: 'Prehánky',
  82: 'Silné prehánky',
  85: 'Snehové prehánky',
  86: 'Silné snehové prehánky',
  95: 'Búrka',
  96: 'Búrka s krúpami',
  99: 'Silná búrka s krúpami',
};

/** Svetove strany; `WindDirection` je smer, ODKIAL vietor fuka. */
const STRANY = ['S', 'SV', 'V', 'JV', 'J', 'JZ', 'Z', 'SZ'];

/**
 * Hodnota jedneho merania zariadenia; `undefined`, ked ju nemame.
 *
 * Pouziva sa aj na dlazdici v zozname zariadeni, nie len v detaile -
 * preto je to zvlast.
 */
export function meranie(device: Zariadenie, name: string): { readonly popis: string } | undefined {
  const hodnota = device.state[name];
  if (typeof hodnota !== 'number') return undefined;
  const unit = device.capabilities.find((c) => c.name === name)?.unit;
  return { popis: popisMerania(name, hodnota, unit) };
}

/**
 * Hodnota meranie v citatelnom tvare.
 *
 * Cislo sa zaokruhli na jedno desatinne miesto - `24.800000000000001`
 * z vypoctu nie je udaj, je to sum. Dve schopnosti dostanu slovo
 * navyse, lebo samotne cislo pri nich nic nepovie.
 */
export function popisMerania(name: string, value: number, unit: string | null | undefined): string {
  const cislo = Math.round(value * 10) / 10;
  if (name === 'WeatherCode') {
    const popis = POPIS_POCASIA[value];
    // Neznámy kód sa nevydáva za nič: ukáže sa aspoň číslo.
    return popis === undefined ? String(value) : `${popis} (${String(value)})`;
  }
  if (name === 'WindDirection') {
    const strana = STRANY[Math.round((value % 360) / 45) % 8] ?? '';
    return `${String(cislo)}° ${strana}`;
  }
  return unit === null || unit === undefined ? String(cislo) : `${String(cislo)} ${unit}`;
}

/**
 * Merania zariadenia.
 *
 * Ukazuje KAZDU citatelnu schopnost, ktorej posledna hodnota je cislo,
 * a jednotku berie z API. Predtym tu bol natvrdo napisany zoznam osmich
 * jednotiek, takze zo zariadeni pocasia bolo vidno len teplotu
 * a vlhkost - tlak, vietor, ziarenie ani zrazky sa neukazali vobec
 * (ziadost zadavatela 2026-10-10).
 */
export function Merania({ device }: { readonly device: Zariadenie }): ReactNode {
  const merania = device.capabilities
    .filter((c) => c.access === 'read')
    .map((c) => ({ name: c.name, unit: c.unit, value: device.state[c.name] }))
    .filter(
      (m): m is { name: string; unit: string | null | undefined; value: number } =>
        typeof m.value === 'number',
    );

  if (merania.length === 0) return null;

  return (
    <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {merania.map((m) => (
        <div key={m.name} className="flex min-w-0 flex-col">
          <dt className="text-xs text-(--color-text-subtle)">{nazovSchopnosti(m.name)}</dt>
          <dd className="text-lg font-medium break-words text-(--color-text)">
            {popisMerania(m.name, m.value, m.unit)}
          </dd>
        </div>
      ))}
    </dl>
  );
}
